import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "./Home.css";
import DashboardView from "../DashboardView";
import { Button, Dropdown } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import StatIcon1 from "../../assets/icons/folder.svg";
import StatIcon2 from "../../assets/icons/stat-icon3.svg";
import StatIcon3 from "../../assets/icons/dashboard.svg";
import DashboardIcon from "../../assets/icons/grid.svg";
import GroupIcon from "../../assets/icons/folder-plus.svg";
import ResourceIcon from "../../assets/icons/database.svg";

import DataSourceDonut from "./Charts/DataSourceChart";
import PublicationOverview from "./PublicationOverview/PublicationOverview";
import ScheduledReportsOverview from "./ScheduledReportsOverview/ScheduledReports";
import SelectItemsTable from "./SelectItemTable/SelectItemTable";
import { fetchHomeSummary, fetchCreatorNames, statAmounts, donutData, recentItems, publicationData } from "./homeApi";
import { fetchHomeSchedules, calendarItems } from "./homeSchedules";

/* Stable identity for the "nothing loaded yet" cases — these feed effect
   dependency arrays and memo inputs, and a fresh [] on every render would
   re-run them forever. */
const NO_USERS = [];
const NO_APPS = [];

export default function Home({ onAddGroup, onAddResource, onAddDashboard }) {
  const history = useHistory();
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);

  /* Two independent requests, deliberately not combined:

       /home-summary        ours — stat tiles, donut, Publication Overview, Recent
       /v1/scheduler/all    the scheduler service — calendar and schedules table

     They fail independently too. The scheduler being down should leave the
     stat tiles and the donut showing their numbers, not blank the page. */
  const [summary, setSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [schedules, setSchedules] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);
  const [schedulesError, setSchedulesError] = useState(null);

  /* Creator names the cached org list could not supply, resolved id by id from
     the Org Registry — see fetchCreatorNames. Filled in after the table has
     already rendered, so the rows appear immediately and the odd name arrives a
     moment later rather than the whole table waiting on it. */
  const [creatorNames, setCreatorNames] = useState({});

  /* The Created By column needs names, and DashboardView is already fetching
     every user in the org into redux. Read from there rather than asking the org
     registry again for the ones it covers.

     The fallback is a module constant, not a fresh `[]`: this feeds an effect's
     dependency array, and a new array identity on every render would re-run it
     forever. */
  const otherUsers = useSelector((state) => state.user.otherUsers) || NO_USERS;

  /* Publication Overview needs application *names*; /home-summary only knows
     ids. DashboardView already loads the App Registry into redux — the same
     list the Resources page maps its Application column from. */
  const apps = useSelector((state) => state.user.apps && state.user.apps.Result) || NO_APPS;

  /* DashboardView fetches the registry in parallel with this page's own
     request, so the summary regularly arrives first. Rendered then, the card
     labels its bars with raw application ids and swaps them for names a moment
     later — a visible flicker on every reload.

     `apps` being empty cannot tell "still loading" from "this user has none",
     so the card waits on the thunk's own status instead. An outright failure
     counts as resolved: the ids are a poor label but a better outcome than a
     card that never stops loading. */
  const appsState = useSelector((state) => state.user.appsState);
  const appsResolved = appsState === "success" || appsState === "error";

  useEffect(() => {
    /* The page can unmount while the request is out — logging out lands on
       /login, and setState on a gone component warns. */
    let active = true;

    setIsLoadingSummary(true);
    setSummaryError(null);

    fetchHomeSummary()
      .then((data) => {
        if (!active) return;
        setSummary(data);
      })
      .catch((error) => {
        if (!active) return;
        console.log("home summary failed===>>", error);
        setSummaryError(error);
      })
      .finally(() => {
        if (active) setIsLoadingSummary(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    setIsLoadingSchedules(true);
    setSchedulesError(null);

    fetchHomeSchedules()
      .then((rows) => {
        if (active) setSchedules(rows);
      })
      .catch((error) => {
        if (!active) return;
        console.log("home schedules failed===>>", error);
        setSchedules([]);
        setSchedulesError(error);
      })
      .finally(() => {
        if (active) setIsLoadingSchedules(false);
      });

    return () => {
      active = false;
    };
  }, []);

  /* Runs after the summary lands, and again if the org user list arrives later —
     DashboardView fetches it in parallel with this page's own request, so which
     of the two wins is a race. Ids the list already covers are skipped inside
     fetchCreatorNames, so the second pass is usually no requests at all. */
  useEffect(() => {
    if (!summary) return undefined;

    let active = true;

    fetchCreatorNames(summary.recent, otherUsers).then((names) => {
      if (active) setCreatorNames(names);
    });

    return () => {
      active = false;
    };
  }, [summary, otherUsers]);

  const amounts = statAmounts(summary && summary.stats);

  /* Icons and colours are the page's, the numbers are the API's. A tile shows
     "—" until its number arrives, rather than a 0 that would read as a real
     count of nothing. */
  const dashboardStats = [
    {
      statName: "Total Groups Created",
      amount: amounts.groups,
      icon: StatIcon1,
      bgColor: "#FFFBEB",
      iconBgColor: "#FEF3C7",
      borderColor: "#FEF3C7",
    },
    {
      statName: "Total Resources Added",
      amount: amounts.resources,
      icon: StatIcon2,
      bgColor: "#ECFDF5",
      iconBgColor: "#D1FAE5",
      borderColor: "#D1FAE5",
    },
    {
      statName: "Total Reports/Dashboards",
      amount: amounts.boards,
      icon: StatIcon3,
      bgColor: "#F0F9FF",
      iconBgColor: "#E0F2FE",
      borderColor: "#E0F2FE",
    },
  ];


  /* Built from the resources that actually exist, so a type nobody uses is
     simply absent — MySQL is not in the data today and returns on its own if a
     connection is added, with no change here.

     The mock it replaced, for reference:
       [{ name: "REST API", value: 45, color: "#1E3A5F" },
        { name: "MySQL",    value: 30, color: "#F59E0B" },
        { name: "GraphQL",  value: 15, color: "#94A3B8" },
        { name: "MongoDB",  value: 10, color: "#2DD4BF" }] */
  const dataSourceData = donutData(summary && summary.dataSourceUsage);

  /* One calendar entry per upcoming run of every published schedule, expanded
     from the recurrence rules — see homeSchedules.js. Replaces a mock array of
     ten invented meetings and presentations. */
  const scheduledReportsData = calendarItems(schedules);

  const publicationRows = publicationData(
    summary && summary.publicationOverview,
    summary && summary.publishedTotal,
    apps,
  );

  /* dashboardItems was declared here and never rendered - the Recent
     Dashboards & Reports table gets its rows from /home-summary now (see
     recentItems in homeApi.js), and nothing else referenced it. */

  const handleAddGroup = () => {
    setIsCreateMenuOpen(false);
    if (onAddGroup) onAddGroup();
  };

  const handleAddResource = () => {
    setIsCreateMenuOpen(false);
    if (onAddResource) onAddResource();
  };

  const handleAddDashboard = () => {
    setIsCreateMenuOpen(false);
    if (onAddDashboard) onAddDashboard("dashboard"); // or "reports" based on selection
  };

  const createMenuItems = [
    { key: "dashboard", label: "Dashboard / Report", icon: DashboardIcon, onClick: handleAddDashboard },
    { key: "resource", label: "Resource", icon: ResourceIcon, onClick: handleAddResource },
    { key: "group", label: "Group", icon: GroupIcon, onClick: handleAddGroup },
  ];

  const addNewMenu = (
    <div className="create-menu">
      <div className="create-menu-title">Create</div>
      {createMenuItems.map((item) => (
        <div key={item.key} className="create-menu-item" onClick={item.onClick}>
          <span className={`create-menu-icon create-menu-icon--${item.key}`}>
            <i style={{ WebkitMaskImage: `url(${item.icon})`, maskImage: `url(${item.icon})` }} />
          </span>
          <span className="create-menu-label">{item.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="dashboard-container">
      <div className="dashboard-header-container">
        <h2>Home</h2>
        <div>
          <Dropdown
            overlay={addNewMenu}
            trigger={["click"]}
            placement="bottomRight"
            visible={isCreateMenuOpen}
            onVisibleChange={setIsCreateMenuOpen}
            overlayClassName="create-menu-overlay"
          >
            <Button type="primary" className="add-new-btn">
              <PlusOutlined /> Create New
            </Button>
          </Dropdown>
        </div>
      </div>

      <div className="dashboard-stats">
        {dashboardStats.map((stat, index) => (
          <div
            key={index}
            className="stat-container"
            style={{ backgroundColor: stat.bgColor, border: `1px solid ${stat.borderColor}` }}
          >
            <div className="stat-content">
              <div className="stat-text">
                <h3 className="stat-name">{stat.statName}</h3>
                {/* A shimmer while the request is out, an em dash if it failed.
                    Never a 0 for either — that would read as a real count of
                    nothing. */}
                {isLoadingSummary ? (
                  <div className="stat-amount-skeleton" aria-label="Loading" />
                ) : (
                  <div className="stat-amount">{stat.amount === null ? "—" : stat.amount}</div>
                )}
              </div>
              <div className="stat-icon" style={{ backgroundColor: stat.iconBgColor }}>
                <img src={stat.icon} alt="stat icon" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* The Most Used Dashboards slider stood here. Removed — nothing records
          that a board was opened, so its view counts could only ever be
          invented. If it comes back it needs view tracking behind it first. */}

      {/* Three cards in one row on a wide screen, in the order they are read:
          the donut, Publication Overview, then Recent. As the window narrows
          they degrade in steps rather than all at once — see the grid rules in
          Home.css, which give Recent the full width first and only then stack
          the other two. */}
      <div className="dashboard-chart-section">
        <div className="data-source-usage">
          <div className="data-source-usage-header">
            <h3>Data Source Usage</h3>
            <p>Details</p>
          </div>
          <DataSourceDonut dataSourceData={dataSourceData} loading={isLoadingSummary} error={summaryError} />
        </div>

        {/* Held in its loading state until the app names are available as well
            as the counts — a bar labelled with a raw id is worse than one that
            has not appeared yet. */}
        <PublicationOverview
          rows={publicationRows}
          loading={isLoadingSummary || !appsResolved}
          error={summaryError}
        />

        <div className="dashboard-recent-cell">
          <SelectItemsTable
            items={recentItems(summary && summary.recent, otherUsers, creatorNames)}
            loading={isLoadingSummary}
            error={summaryError}
            onViewAll={() => history.push("/dashboard-list")}
          />
        </div>
      </div>

      {/* A second, separate Scheduled Reports table sat below this. Removed — it
          listed the same schedules the panel beside the calendar already shows,
          and its View All has moved into that panel's header. */}
      <div className="dashboard-full-row">
        <ScheduledReportsOverview
          scheduledReportsData={scheduledReportsData}
          loading={isLoadingSchedules}
          error={schedulesError}
          onViewAll={() => history.push("/scheduled-reports")}
        />
      </div>
    </div>
  );
}
